import React, { useState, useEffect } from "react";
import { Container, Table, Form, Button, Spinner, Alert } from "react-bootstrap";
import AdminService from "../../services/admin.service";

const AdminIgniteInventory = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [inventoryChanges, setInventoryChanges] = useState({});

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            setLoading(true);
            const data = await AdminService.getIgniteProductsWithInventory();
            setProducts(data);
            setLoading(false);
        } catch (err) {
            setError("Error loading products");
            setLoading(false);
        }
    };

    const handleQuantityChange = (productId, quantity) => {
        setInventoryChanges({
            ...inventoryChanges,
            [productId]: parseInt(quantity) || 0
        });
    };

    const getDisplayQuantity = (product) => {
        if (inventoryChanges.hasOwnProperty(product.id)) {
            return inventoryChanges[product.id];
        }
        return product.ignite_inventory?.quantity || 0;
    };

    const saveAllChanges = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            const updates = Object.entries(inventoryChanges).map(([productId, quantity]) => ({
                productId: parseInt(productId),
                quantity: quantity
            }));

            if (updates.length === 0) {
                setError("No changes to save");
                setSaving(false);
                return;
            }

            await AdminService.bulkUpdateIgniteInventory(updates);
            setSuccess("Inventory updated successfully!");
            setInventoryChanges({});
            await loadProducts();
            setSaving(false);
        } catch (err) {
            setError("Error saving inventory");
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container className="mt-4">
                <Spinner animation="border" /> Loading Ignite Products...
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <h2>Ignite Product Inventory</h2>
            <p>Update inventory quantities for Ignite products below.</p>

            {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

            <div className="mb-3">
                <Button 
                    variant="primary" 
                    onClick={saveAllChanges} 
                    disabled={saving || Object.keys(inventoryChanges).length === 0}
                >
                    {saving ? <><Spinner size="sm" /> Saving...</> : "Save All Changes"}
                </Button>
                <span className="ms-3 text-muted">
                    {Object.keys(inventoryChanges).length} changes pending
                </span>
            </div>

            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Product</th>
                        <th>Color</th>
                        <th>Size</th>
                        <th>Current Quantity</th>
                        <th>New Quantity</th>
                    </tr>
                </thead>
                <tbody>
                    {products.map(product => (
                        <tr key={product.id}>
                            <td>{product.id}</td>
                            <td>{product.style?.title || 'N/A'}</td>
                            <td>{product.color?.colorName || 'N/A'}</td>
                            <td>{product.size?.sizeName || 'N/A'}</td>
                            <td>{product.ignite_inventory?.quantity || 0}</td>
                            <td>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    value={getDisplayQuantity(product)}
                                    onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                                    style={{ width: '100px' }}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            {products.length === 0 && (
                <Alert variant="info">No Ignite products found.</Alert>
            )}
        </Container>
    );
};

export default AdminIgniteInventory;

